import { ComponentProps } from "react";
import Card from "@particles/card/Card";
import { FeedMedia } from "@routes/Article/utils/types";
import { FeedVideosItemType } from "@routes/Videos/utils/types";
import { SanitizedFeedSectionArticle } from "@typings/Cue";
import { getUrlQuery } from "@util/helpers";

/**
 * Returns the appropriate size
 * @param {string} isScreenXL - Boolean value to identify XL screen
 * @returns size number of items to render on component based on screen size
 */
export const group = (isScreenXL: boolean) => {
  if (isScreenXL) return 2;

  return 3;
};

/**
 * Returns the appropriate size of video card
 * @param isScreenLG
 * @param isScreenMD
 * @returns width of the slider card in percentage string
 */
export const getSliderCardSize = (isScreenLG: boolean, isScreenMD: boolean) => {
  if (isScreenLG) return "25%";
  if (!isScreenLG && isScreenMD) return "40%";

  return "70%";
};

/** Function converts the media type data to feedMedia type */

export const transformMediaToFeedMedia = (
  data: FeedVideosItemType
): FeedMedia | undefined => {
  const posterData = data?.media[0].poster;
  const id = data.id;
  const description = data.description;
  const name = data.name;

  const { url, width, height } = posterData;

  if (!url) return undefined;

  return {
    id: id,
    type: "picture",
    title: name,
    description: description,
    landscape: {
      url: url,
      width: width,
      height: height,
    },
  };
};

/**
 * Returns the first highlight card props for with campaign template.
 * @param articles - The articles to be displayed in the component.
 * @param ref - The reference to be used for the link.
 * @returns {ComponentProps<typeof Card>[]} - The card props first highlight card props for with campaign template.
 */
export const getHighlightCardProps = (
  articles: SanitizedFeedSectionArticle[],
  size?: number,
  ref?: string
): ComponentProps<typeof Card>[] => {
  const linkParams = ref ? new URLSearchParams({ ref }) : undefined;
  const articlesToProcess = size ? articles.slice(0, size) : articles;

  const cardProps = articlesToProcess.map(
    ({ id, title, urlPath, media, kicker, sections, blurb }, index) => {
      const mainSection = sections?.[0];
      const mainMedia = media[0];
      const imgSrc = mainMedia?.landscape?.url;
      const imgWidth = mainMedia?.landscape?.width;
      const imgHeight = mainMedia?.landscape?.height;
      const _defaultImage =
        index === 0
          ? { directoryName: mainSection?.uniqueName || "" }
          : undefined;

      const props: ComponentProps<typeof Card> = {
        id,
        title,
        link: urlPath + getUrlQuery(urlPath, linkParams),
        thumbnail:
          imgSrc && imgWidth && imgHeight && index === 0
            ? {
                src: imgSrc,
                alt: mainMedia.alt || mainMedia.caption || "",
                width: imgWidth,
                height: imgHeight,
              }
            : undefined,
        kicker:
          index !== 0 ? kicker?.fields?.[0]?.value?.toUpperCase() : undefined,
        blurb: index === 0 ? blurb : undefined,
        defaultImage: _defaultImage,
      };

      return props;
    }
  );

  return JSON.parse(JSON.stringify(cardProps));
};

/**
 * Returns the views card props for with campaign template.
 * @param articles - The articles to be displayed in the component.
 * @param ref - The reference to be used for the link.
 * @returns {ComponentProps<typeof Card>[]} - The card props views card props for with campaign template.
 */
export const getViewsCardProps = (
  articles: SanitizedFeedSectionArticle[],
  size?: number,
  ref?: string
): ComponentProps<typeof Card>[] => {
  const linkParams = ref ? new URLSearchParams({ ref }) : undefined;
  const articlesToProcess = size ? articles.slice(0, size) : articles;

  const cardProps = articlesToProcess.map(
    ({ id, title, urlPath, blurb, authors }) => {
      const props: ComponentProps<typeof Card> = {
        id,
        title,
        link: urlPath + getUrlQuery(urlPath, linkParams),
        blurb,
        authors: authors,
      };

      return props;
    }
  );

  return JSON.parse(JSON.stringify(cardProps));
};

/**
 * Returns the Latest News card props for with campaign template.
 * @param articles - The articles to be displayed in the component.
 * @param ref - The reference to be used for the link.
 * @returns {ComponentProps<typeof Card>[]} - The card props Latest News card props for with campaign template.
 */
export const getLatestNewsCardProps = (
  articles: SanitizedFeedSectionArticle[],
  size?: number,
  ref?: string
): ComponentProps<typeof Card>[] => {
  const linkParams = ref ? new URLSearchParams({ ref }) : undefined;
  const articlesToProcess = size ? articles.slice(0, size) : articles;

  const cardProps = articlesToProcess.map(({ id, title, urlPath }) => {
    const props: ComponentProps<typeof Card> = {
      id,
      title,
      link: urlPath + getUrlQuery(urlPath, linkParams),
    };

    return props;
  });

  return JSON.parse(JSON.stringify(cardProps));
};

/**
 * Returns the Latest News card props for with campaign template.
 * @param articles - The articles to be displayed in the component.
 * @param ref - The reference to be used for the link.
 * @returns {ComponentProps<typeof Card>[]} - The card props Latest News card props for with campaign template.
 */
export const getPodcastsCardProps = (
  articles: SanitizedFeedSectionArticle[],
  size?: number,
  ref?: string
): ComponentProps<typeof Card>[] => {
  const linkParams = ref ? new URLSearchParams({ ref }) : undefined;
  const articlesToProcess = size ? articles.slice(0, size) : articles;

  const cardProps = articlesToProcess.map(
    ({ id, title, urlPath, media, sections }) => {
      const mainMedia = media[0];
      const imgSrc = mainMedia?.landscape?.url;
      const imgWidth = mainMedia?.landscape?.width;
      const imgHeight = mainMedia?.landscape?.height;
      const mainSection = sections?.[0];

      const props: ComponentProps<typeof Card> = {
        id,
        title,
        link: urlPath + getUrlQuery(urlPath, linkParams),
        thumbnail:
          imgSrc && imgWidth && imgHeight
            ? {
                src: imgSrc,
                alt: mainMedia.alt || mainMedia.caption || "",
                width: imgWidth,
                height: imgHeight,
              }
            : undefined,
        defaultImage: { directoryName: mainSection?.uniqueName || "" },
      };

      return props;
    }
  );

  return JSON.parse(JSON.stringify(cardProps));
};

/**
 * Returns the first highlight card props for with highlight template.
 * @param articles - The articles to be displayed in the component.
 * @param ref - The reference to be used for the link.
 * @returns {ComponentProps<typeof Card>[]} - The card props first highlight card props for with highlight template.
 */
export const getRelatedStoriesCardProps = (
  articles: SanitizedFeedSectionArticle[],
  size?: number,
  ref?: string
): ComponentProps<typeof Card>[] => {
  const linkParams = ref ? new URLSearchParams({ ref }) : undefined;
  const articlesToProcess = size ? articles.slice(0, size) : articles;

  const cardProps = articlesToProcess.map(
    ({
      id,
      title,
      urlPath,
      media,
      kicker,
      sections,
      blurb,
      edited,
      updated,
    }) => {
      const mainSection = sections?.[0];
      const mainMedia = media[0];
      const imgSrc = mainMedia?.landscape?.url;
      const imgWidth = mainMedia?.landscape?.width;
      const imgHeight = mainMedia?.landscape?.height;

      const props: ComponentProps<typeof Card> = {
        id,
        title,
        link: urlPath + getUrlQuery(urlPath, linkParams),
        thumbnail:
          imgSrc && imgWidth && imgHeight
            ? {
                src: imgSrc,
                alt: mainMedia.alt || mainMedia.caption || "",
                width: imgWidth,
                height: imgHeight,
              }
            : undefined,
        kicker: kicker?.fields?.[0]?.value?.toUpperCase(),
        blurb,
        defaultImage: { directoryName: mainSection?.uniqueName || "" },
        hasUpdatedTimestampDisplay: true,
        editedTime: edited,
        updatedTime: updated,
      };

      return props;
    }
  );

  return JSON.parse(JSON.stringify(cardProps));
};
